import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Toast } from "primereact/toast";
import {
  FaSyncAlt,
  FaSearch,
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaCheck,
  FaTimes,
  FaTrash,
  FaBan,
  FaUnlock,
  FaCheckCircle,
  FaYoutube,
  FaInstagram,
  FaFacebook,
  FaImage,
  FaRupeeSign,
  FaInbox,
  FaCopy,
  FaChartPie,
  FaCalendarCheck,
  FaUserTie,
  FaWallet,
  FaClock,
  FaExternalLinkAlt,
  FaBolt,
  FaUserCheck,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";

/* ================= look & feel ================= */

const CSS = `
@keyframes csUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes csFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes csZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
@keyframes csShine { 0% { background-position: -200px 0; } 100% { background-position: calc(200px + 100%) 0; } }
@keyframes csPulse { 0% { box-shadow: 0 0 0 0 rgba(245,158,11,.55); } 70% { box-shadow: 0 0 0 8px rgba(245,158,11,0); } 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); } }
.cs-up { animation: csUp .45s cubic-bezier(.2,.7,.2,1) both; }
.cs-fade { animation: csFade .2s ease both; }
.cs-zoom { animation: csZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.cs-hot { animation: csPulse 1.8s ease-out infinite; }
.cs-skel { background: linear-gradient(90deg, rgba(148,163,184,.15) 0, rgba(148,163,184,.3) 100px, rgba(148,163,184,.15) 200px); background-size: 200px 100%; animation: csShine 1.2s linear infinite; border-radius: 12px; }
.cs-btn { transition: transform .15s ease, filter .2s ease, box-shadow .2s ease; }
.cs-btn:hover { filter: brightness(1.08); transform: translateY(-1px); }
.cs-btn:active { transform: scale(.96); }
.cs-card { transition: transform .2s ease, box-shadow .25s ease; }
.cs-card:hover { transform: translateY(-2px); box-shadow: 0 16px 30px -18px rgba(15,23,42,.45); }
@media (prefers-reduced-motion: reduce) { .cs-up, .cs-fade, .cs-zoom, .cs-hot, .cs-skel { animation: none; } .cs-btn:hover, .cs-card:hover { transform: none; } }
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500";
const BTN = "cs-btn inline-flex items-center justify-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-bold text-white";
const GRAD = "linear-gradient(135deg,#7c3aed 0%,#c026d3 55%,#ec4899 100%)";

const STATUS = {
  review: { label: "Payment to check", cls: "bg-amber-100 text-amber-800", bar: "#f59e0b" },
  approved: { label: "Approved", cls: "bg-sky-100 text-sky-700", bar: "#0ea5e9" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700", bar: "#10b981" },
  rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-700", bar: "#f43f5e" },
  pending: { label: "New application", cls: "bg-amber-100 text-amber-800", bar: "#f59e0b" },
  suspended: { label: "Suspended", cls: "bg-slate-200 text-slate-700", bar: "#64748b" },
};

const BOOKING_FILTERS = [
  { key: "review", label: "To check", icon: FaClock },
  { key: "approved", label: "Approved", icon: FaCheck },
  { key: "completed", label: "Completed", icon: FaCheckCircle },
  { key: "rejected", label: "Rejected", icon: FaTimes },
  { key: "", label: "All", icon: FaInbox },
];
const MENTOR_FILTERS = [
  { key: "pending", label: "Applications" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "suspended", label: "Suspended" },
];

const REJECT_BOOKING = ["Payment not received in our account", "UTR is wrong or unclear", "Screenshot is not clear", "Amount does not match", "Duplicate booking"];
const REJECT_MENTOR = ["Profile photo is not clear", "Social links could not be verified", "Introduction is too short", "Not eligible right now"];
const SUSPEND_MENTOR = ["Policy violation", "User complaints", "Inactive for a long time"];

/* ================= helpers ================= */

const rs = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const wa = (p) => {
  const d = String(p || "").replace(/\D/g, "").slice(-10);
  return d ? `https://wa.me/91${d}` : null;
};
const mailTo = (email, subject, body) => `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
const initials = (n) =>
  String(n || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((x) => x[0])
    .join("")
    .toUpperCase();
const ago = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return s;
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)} hr ago`;
  if (m < 43200) return `${Math.floor(m / 1440)} days ago`;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const MODE = { message: "Message", voice: "Voice call", video: "Video call", meet: "In person" };
const SPEED = { m1: "Within 1 month", d7: "Within 7 days", h48: "Within 48 hours", h24: "Within 24 hours", urgent: "Urgent" };
const SERVICE = { fix: "Fix a problem", create: "Create / start", growth: "Growth" };

/* ================= small parts ================= */

const Pill = ({ s }) => {
  const m = STATUS[s] || { label: s, cls: "bg-slate-100 text-slate-700" };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold ${m.cls}`}>{m.label}</span>;
};

const Avatar = ({ src, name, size = 48 }) =>
  src ? (
    <img src={src} alt="" style={{ width: size, height: size }} className="rounded-full object-cover shrink-0 ring-2 ring-white dark:ring-slate-700" />
  ) : (
    <div style={{ width: size, height: size, background: GRAD }} className="rounded-full shrink-0 flex items-center justify-center text-white font-extrabold">
      {initials(name)}
    </div>
  );

const Info = ({ icon: Icon, label, value, copy, onCopy, wide }) =>
  value ? (
    <div className={`flex items-start gap-2 text-sm ${wide ? "md:col-span-2" : ""}`}>
      <Icon className="mt-1 text-slate-400 shrink-0" size={12} />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{label}</div>
        <div className="text-slate-900 dark:text-slate-100 break-words flex items-center gap-2">
          {value}
          {copy ? (
            <button onClick={() => onCopy(String(value))} className="text-slate-400 hover:text-violet-600" title="Copy">
              <FaCopy size={11} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  ) : null;

const Steps = ({ status }) => {
  if (status === "rejected") {
    return (
      <div className="flex items-center gap-2 text-xs font-bold text-rose-600">
        <FaTimes /> Rejected
      </div>
    );
  }
  const order = ["review", "approved", "completed"];
  const at = order.indexOf(status);
  const names = ["Payment check", "Approved", "Completed"];
  return (
    <div className="flex items-center gap-1.5">
      {names.map((n, i) => (
        <React.Fragment key={n}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold ${i <= at ? "text-violet-600" : "text-slate-400"}`}>
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] ${i <= at ? "bg-violet-600" : "bg-slate-300"}`}>{i < at ? <FaCheck /> : i + 1}</span>
            {n}
          </div>
          {i < names.length - 1 ? <div className={`h-0.5 w-5 rounded ${i < at ? "bg-violet-500" : "bg-slate-200"}`} /> : null}
        </React.Fragment>
      ))}
    </div>
  );
};

const Contact = ({ phone, email, subject, body }) => (
  <div className="flex flex-wrap gap-2">
    {phone ? (
      <a href={`tel:${phone}`} className={`${BTN} bg-emerald-600`}>
        <FaPhoneAlt /> Call
      </a>
    ) : null}
    {wa(phone) ? (
      <a href={wa(phone)} target="_blank" rel="noreferrer" className={`${BTN} bg-green-700`}>
        <FaWhatsapp /> WhatsApp
      </a>
    ) : null}
    {email ? (
      <a href={mailTo(email, subject, body)} className={`${BTN} bg-sky-600`}>
        <FaEnvelope /> Email reply
      </a>
    ) : null}
  </div>
);

const Empty = ({ text, loading }) =>
  loading ? (
    <div className="grid gap-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="cs-skel h-32" />
      ))}
    </div>
  ) : (
    <div className={`${CARD} p-12 text-center text-slate-500`}>
      <FaInbox className="mx-auto text-4xl mb-3 opacity-40" />
      <div className="font-semibold">{text}</div>
    </div>
  );

/* ================= booking card ================= */

const BookingCard = ({ b, ask, call, reload, say, setShot, copy }) => {
  const u = b.user || {};
  const blocked = String(u.account_status).toLowerCase() === "block";
  const bar = (STATUS[b.status] || {}).bar || "#94a3b8";
  const subj = `YCNP Creator Support - booking #${b.id}`;
  const body =
    b.status === "approved"
      ? `Hello ${b.full_name},\n\nYour payment of ${rs(b.amount)} for booking #${b.id} is verified. We will contact you soon on ${b.phone}.\n\nRegards,\nYCNP Support`
      : b.status === "rejected"
      ? `Hello ${b.full_name},\n\nSorry, your booking #${b.id} could not be accepted.\nReason: ${b.admin_note || ""}\n\nRegards,\nYCNP Support`
      : `Hello ${b.full_name},\n\nAbout your booking #${b.id} (${rs(b.amount)}):\n\n\nRegards,\nYCNP Support`;

  const doAct = async (to, n, okMsg) => {
    const d = await call("booking_update", { booking_id: b.id, to, note: n });
    if (d.status) {
      say("success", okMsg);
      reload();
    } else say("error", d.error || "Failed");
  };

  return (
    <div className={`${CARD} cs-card cs-up overflow-hidden`} style={{ borderLeft: `5px solid ${bar}` }}>
      <div className="p-4 md:p-5">
        <div className="flex flex-wrap items-start gap-3 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={b.full_name} />
            <div className="min-w-0">
              <div className="font-extrabold text-slate-900 dark:text-white text-base truncate">{b.full_name}</div>
              <div className="text-xs text-slate-500">
                Booking #{b.id} · {ago(b.created_at)}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-extrabold" style={{ color: "#7c3aed" }}>
              {rs(b.amount)}
            </div>
            <Pill s={b.status} />
          </div>
        </div>

        <div className="mt-3">
          <Steps status={b.status} />
        </div>

        <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
          <Info icon={FaPhoneAlt} label="Phone" value={b.phone} copy onCopy={copy} />
          <Info icon={FaEnvelope} label="Email" value={u.email} copy onCopy={copy} />
          <Info icon={FaUserCheck} label="App user" value={u.name ? `${u.name}${blocked ? " (BLOCKED)" : ""}` : u.user_id} />
          <Info icon={FaUserTie} label="Support from" value={b.mentor_id > 0 ? `Influencer: ${b.mentor_name || "#" + b.mentor_id}` : "Founder (Vishal Kumar)"} />
          <Info icon={FaBolt} label="Service" value={`${SERVICE[b.service_type] || b.service_type} · ${MODE[b.mode] || b.mode}`} />
          <Info icon={FaClock} label="Speed" value={SPEED[b.speed] || b.speed} />
          <Info icon={FaChartPie} label="Platforms" value={String(b.platforms || "").split(",").join(", ")} />
          <Info icon={FaCalendarCheck} label="Preferred time" value={[b.pref_date, b.pref_time].filter(Boolean).join("  ")} />
          <Info icon={FaWallet} label="UTR (payment reference)" value={b.utr} copy onCopy={copy} />
          <Info icon={FaBolt} label="Topic chosen" value={b.problem_type} />
          <Info icon={FaExternalLinkAlt} label="Meeting address" value={b.address} wide />
          {b.status === "completed" ? <Info icon={FaRupeeSign} label="Split" value={`Influencer ${rs(b.mentor_share)} · You ${rs(b.owner_share)}`} /> : null}
          <Info icon={FaEnvelope} label="Your note to user" value={b.admin_note} wide />
        </div>

        {b.problem ? (
          <div className="mt-3 rounded-xl border border-violet-200 dark:border-violet-900 bg-violet-50/60 dark:bg-violet-950/30 p-3">
            <div className="text-[11px] uppercase tracking-wide font-bold text-violet-600 mb-1">User's problem</div>
            <div className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">{b.problem}</div>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {b.screenshot_url ? (
            <button onClick={() => setShot(b.screenshot_url)} className={`${BTN} bg-slate-800`}>
              <FaImage /> Payment screenshot
            </button>
          ) : null}
          <Contact phone={b.phone} email={u.email} subject={subj} body={body} />
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 flex flex-wrap gap-2">
          {b.status === "review" ? (
            <button className={`${BTN} bg-emerald-600`} onClick={() => ask({ title: `Approve payment of ${rs(b.amount)}?`, hint: `Check ${rs(b.amount)} is in your account with UTR ${b.utr || "-"}`, okLabel: "Approve payment", tone: "emerald", onOk: (n) => doAct("approved", n, "Booking approved") })}>
              <FaCheck /> Approve payment
            </button>
          ) : null}
          {b.status === "approved" ? (
            <button className={`${BTN} bg-violet-600`} onClick={() => ask({ title: "Support finished?", hint: "Earnings are added to the influencer now. This cannot be undone.", okLabel: "Mark completed", tone: "violet", onOk: (n) => doAct("completed", n, "Marked completed") })}>
              <FaCheckCircle /> Mark completed
            </button>
          ) : null}
          {b.status === "review" || b.status === "approved" ? (
            <button className={`${BTN} bg-rose-600`} onClick={() => ask({ title: "Reject this booking", needNote: true, quick: REJECT_BOOKING, okLabel: "Reject", tone: "rose", onOk: (n) => doAct("rejected", n, "Booking rejected") })}>
              <FaTimes /> Reject
            </button>
          ) : null}
          <button
            className={`${BTN} ${blocked ? "bg-teal-600" : "bg-orange-600"}`}
            onClick={() =>
              ask({
                title: blocked ? `Unblock ${u.name || "user"}?` : `Block ${u.name || "this user"} from the whole app?`,
                hint: blocked ? "" : "The user will get a notification and lose access.",
                okLabel: blocked ? "Unblock" : "Block user",
                tone: "orange",
                onOk: async () => {
                  const d = await call("user_block", { user_id: b.user_id, to: blocked ? "Live" : "Block" });
                  if (d.status) {
                    say("success", blocked ? "User unblocked" : "User blocked");
                    reload();
                  } else say("error", d.error);
                },
              })
            }>
            {blocked ? <FaUnlock /> : <FaBan />} {blocked ? "Unblock user" : "Block user"}
          </button>
          <button
            className={`${BTN} bg-slate-500 ml-auto`}
            onClick={() =>
              ask({
                title: `Delete booking #${b.id} forever?`,
                hint: "This removes the booking and its review.",
                okLabel: "Delete",
                tone: "rose",
                onOk: async () => {
                  let d = await call("booking_delete", { booking_id: b.id });
                  if (d.need_force && window.confirm(d.error + "\nDelete anyway?")) d = await call("booking_delete", { booking_id: b.id, force: 1 });
                  if (d.status) {
                    say("success", "Deleted");
                    reload();
                  } else if (!d.need_force) say("error", d.error);
                },
              })
            }>
            <FaTrash /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================= influencer card ================= */

const MentorCardAdmin = ({ m, ask, call, reload, say, copy }) => {
  const blocked = String(m.account_status).toLowerCase() === "block";
  const bar = (STATUS[m.status] || {}).bar || "#94a3b8";
  const body = `Hello ${m.name},\n\n\n\nRegards,\nYCNP`;
  const run = async (action, fields, okMsg) => {
    const d = await call(action, fields);
    if (d.status) {
      say("success", okMsg);
      reload();
    } else say("error", d.error || "Failed");
  };
  return (
    <div className={`${CARD} cs-card cs-up overflow-hidden`} style={{ borderLeft: `5px solid ${bar}` }}>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-3">
          <Avatar src={m.photo_url} name={m.name} size={64} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 font-extrabold text-lg text-slate-900 dark:text-white">
              <span className="truncate">{m.name}</span>
              {m.verified ? <FaCheckCircle className="text-sky-500 shrink-0" title="Blue tick" /> : null}
            </div>
            <div className="text-xs text-slate-500">
              {m.city} · applied {ago(m.created_at)}
            </div>
          </div>
          <Pill s={m.status} />
        </div>

        {m.bio ? <div className="mt-3 text-sm text-slate-700 dark:text-slate-200 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-3">{m.bio}</div> : null}

        <div className="mt-3 grid md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
          <Info icon={FaPhoneAlt} label="Phone" value={m.phone} copy onCopy={copy} />
          <Info icon={FaEnvelope} label="Email" value={m.email} copy onCopy={copy} />
          <Info icon={FaWallet} label="Payout UPI" value={m.payout_upi} copy onCopy={copy} />
          <Info icon={FaChartPie} label="Platforms" value={String(m.platforms || "").split(",").join(", ")} />
          <Info icon={FaBolt} label="Services" value={String(m.services || "").split(",").map((s) => SERVICE[s] || s).join(", ")} />
          <Info icon={FaClock} label="Modes" value={String(m.modes || "").split(",").map((s) => MODE[s] || s).join(", ")} />
          <Info icon={FaUserTie} label="Experience" value={m.experience} />
          <Info icon={FaUserTie} label="Languages" value={m.languages} />
          {m.status === "approved" || m.status === "suspended" ? <Info icon={FaRupeeSign} label="Earnings" value={`Earned ${rs(m.earn.earned)} · Paid ${rs(m.earn.paid)} · Due ${rs(m.earn.pending)}`} /> : null}
          <Info icon={FaEnvelope} label="Admin note" value={m.admin_note} wide />
        </div>

        {(m.custom_services || []).length ? (
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-wide font-bold text-slate-400 mb-1">Own services added by influencer</div>
            <div className="flex flex-wrap gap-2">
              {m.custom_services.map((c) => (
                <span key={c} className="px-3 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-700">
                  {c}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2 items-center">
          {m.youtube ? (
            <a href={m.youtube} target="_blank" rel="noreferrer" className={`${BTN} bg-red-600`}>
              <FaYoutube /> YouTube
            </a>
          ) : null}
          {m.instagram ? (
            <a href={m.instagram} target="_blank" rel="noreferrer" className={`${BTN} bg-pink-600`}>
              <FaInstagram /> Instagram
            </a>
          ) : null}
          {m.facebook ? (
            <a href={m.facebook} target="_blank" rel="noreferrer" className={`${BTN} bg-blue-700`}>
              <FaFacebook /> Facebook
            </a>
          ) : null}
          <Contact phone={m.phone} email={m.email} subject="YCNP Influencer Mentor" body={body} />
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 flex flex-wrap gap-2">
          {m.status !== "approved" ? (
            <button className={`${BTN} bg-emerald-600`} onClick={() => ask({ title: `Approve ${m.name}?`, hint: "Their card appears in the app and the blue tick is added.", okLabel: "Approve", tone: "emerald", onOk: (n) => run("mentor_update", { mentor_id: m.id, to: "approved", note: n }, "Approved with blue tick") })}>
              <FaCheck /> Approve
            </button>
          ) : null}
          {m.status === "pending" ? (
            <button className={`${BTN} bg-rose-600`} onClick={() => ask({ title: "Reject application", needNote: true, quick: REJECT_MENTOR, okLabel: "Reject", tone: "rose", onOk: (n) => run("mentor_update", { mentor_id: m.id, to: "rejected", note: n }, "Rejected") })}>
              <FaTimes /> Reject
            </button>
          ) : null}
          {m.status === "approved" ? (
            <>
              <button className={`${BTN} ${m.verified ? "bg-slate-500" : "bg-sky-500"}`} onClick={() => run("mentor_verify", { mentor_id: m.id, verified: m.verified ? 0 : 1 }, m.verified ? "Blue tick removed" : "Blue tick given")}>
                <FaCheckCircle /> {m.verified ? "Remove blue tick" : "Give blue tick"}
              </button>
              <button className={`${BTN} bg-amber-600`} onClick={() => ask({ title: "Suspend this influencer", hint: "Their card disappears from the app.", needNote: true, quick: SUSPEND_MENTOR, okLabel: "Suspend", tone: "amber", onOk: (n) => run("mentor_update", { mentor_id: m.id, to: "suspended", note: n }, "Suspended") })}>
                <FaBan /> Suspend
              </button>
            </>
          ) : null}
          <button className={`${BTN} ${blocked ? "bg-teal-600" : "bg-orange-600"}`} onClick={() => ask({ title: blocked ? "Unblock this user?" : "Block this user from the whole app?", okLabel: blocked ? "Unblock" : "Block user", tone: "orange", onOk: () => run("user_block", { user_id: m.user_id, to: blocked ? "Live" : "Block" }, blocked ? "Unblocked" : "Blocked") })}>
            {blocked ? <FaUnlock /> : <FaBan />} {blocked ? "Unblock user" : "Block user"}
          </button>
          <button className={`${BTN} bg-slate-500 ml-auto`} onClick={() => ask({ title: `Delete ${m.name} permanently?`, hint: "Not allowed while bookings are open or money is due.", okLabel: "Delete", tone: "rose", onOk: () => run("mentor_delete", { mentor_id: m.id }, "Influencer deleted") })}>
            <FaTrash /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================= page ================= */

const CreatorSupportAdmin = () => {
  const { baseUrl } = useBaseUrl();
  const toast = useRef(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [ov, setOv] = useState(null);
  const [urgent, setUrgent] = useState([]);
  const [bFilter, setBFilter] = useState("review");
  const [q, setQ] = useState("");
  const [bookings, setBookings] = useState([]);
  const [mFilter, setMFilter] = useState("pending");
  const [mentors, setMentors] = useState([]);
  const [mCounts, setMCounts] = useState({});
  const [pay, setPay] = useState({ list: [], total_pending: 0, history: [] });
  const [dlg, setDlg] = useState(null);
  const [note, setNote] = useState("");
  const [shot, setShot] = useState("");

  const say = (severity, detail) => toast.current?.show({ severity, summary: severity === "error" ? "Problem" : "Done", detail, life: 4500 });
  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      say("success", "Copied");
    } catch (e) {
      say("error", "Could not copy");
    }
  };

  const call = useCallback(
    async (action, fields = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      try {
        const res = await axios.post(`${baseUrl}/creator_support.php`, fd, { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true });
        return res.data || {};
      } catch (e) {
        return { status: false, error: "Server or internet problem" };
      }
    },
    [baseUrl]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const o = await call("overview");
      if (o.status) setOv(o);
      else if (tab === "overview") say("error", o.error);
      if (tab === "overview") {
        const d = await call("bookings", { filter: "review" });
        if (d.status) setUrgent((d.list || []).slice(0, 5));
      } else if (tab === "bookings") {
        const d = await call("bookings", { filter: bFilter, q });
        if (d.status) setBookings(d.list || []);
        else say("error", d.error);
      } else if (tab === "mentors") {
        const d = await call("mentors", { filter: mFilter });
        if (d.status) {
          setMentors(d.list || []);
          setMCounts(d.counts || {});
        } else say("error", d.error);
      } else if (tab === "payouts") {
        const d = await call("payouts");
        if (d.status) setPay(d);
        else say("error", d.error);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, bFilter, mFilter, q, call]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, bFilter, mFilter]);

  const ask = (cfg) => {
    setNote("");
    setDlg(cfg);
  };

  const payUpi = (p) => {
    window.location.href = `upi://pay?pa=${encodeURIComponent(p.payout_upi)}&pn=${encodeURIComponent(p.name)}&am=${p.pending}&cu=INR&tn=${encodeURIComponent("YCNP influencer payout")}`;
  };

  const TABS = [
    { key: "overview", label: "Overview", icon: FaChartPie, badge: 0 },
    { key: "bookings", label: "Bookings", icon: FaCalendarCheck, badge: ov ? ov.bookings.review : 0 },
    { key: "mentors", label: "Influencers", icon: FaUserTie, badge: ov ? ov.mentors.pending : 0 },
    { key: "payouts", label: "Payouts", icon: FaWallet, badge: 0 },
  ];

  const tone = { emerald: "#059669", rose: "#e11d48", violet: "#7c3aed", orange: "#ea580c", amber: "#d97706" };

  const Stat = ({ icon: Icon, label, value, color, go, hot }) => (
    <button onClick={go || undefined} className={`${CARD} cs-card cs-up text-left p-4 flex items-center gap-3 ${go ? "" : "cursor-default"} ${hot ? "cs-hot" : ""}`}>
      <span className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: color }}>
        <Icon />
      </span>
      <span>
        <span className="block text-2xl font-extrabold text-slate-900 dark:text-white leading-tight">{value}</span>
        <span className="block text-xs font-semibold text-slate-500">{label}</span>
      </span>
    </button>
  );

  return (
    <div className="m-2 md:m-8 mt-20 md:mt-10 p-2 md:p-6">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* header */}
      <div className="rounded-3xl p-5 md:p-7 text-white mb-5 cs-up flex flex-wrap items-center justify-between gap-4" style={{ background: GRAD }}>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest opacity-80">YCNP</div>
          <h1 className="text-2xl md:text-3xl font-extrabold">Creator Support</h1>
          <p className="text-sm opacity-90 mt-1">Payments, bookings, influencers, blue tick, payouts and user control, all in one place.</p>
        </div>
        <div className="flex items-center gap-3">
          {ov ? (
            <div className="text-right hidden sm:block">
              <div className="text-xs opacity-80">Waiting for you</div>
              <div className="text-2xl font-extrabold">{ov.bookings.review + ov.mentors.pending}</div>
            </div>
          ) : null}
          <button onClick={load} className="cs-btn inline-flex items-center gap-2 rounded-xl bg-white/20 hover:bg-white/30 px-4 py-2.5 text-sm font-bold">
            <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        {TABS.map((t) => {
          const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} className={`cs-btn relative inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-extrabold whitespace-nowrap ${on ? "text-white shadow-lg" : "bg-white dark:bg-secondary-dark-bg text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700"}`} style={on ? { background: GRAD } : undefined}>
              <t.icon /> {t.label}
              {t.badge > 0 ? <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] flex items-center justify-center ${on ? "bg-white text-violet-700" : "bg-rose-500 text-white"}`}>{t.badge}</span> : null}
            </button>
          );
        })}
      </div>

      {/* overview */}
      {tab === "overview" ? (
        ov ? (
          <div className="grid gap-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat icon={FaClock} label="Payments to check" value={ov.bookings.review} color="#f59e0b" hot={ov.bookings.review > 0} go={() => { setBFilter("review"); setTab("bookings"); }} />
              <Stat icon={FaCheck} label="Approved, running" value={ov.bookings.approved} color="#0ea5e9" go={() => { setBFilter("approved"); setTab("bookings"); }} />
              <Stat icon={FaCheckCircle} label="Completed" value={ov.bookings.completed} color="#10b981" go={() => { setBFilter("completed"); setTab("bookings"); }} />
              <Stat icon={FaUserTie} label="New applications" value={ov.mentors.pending} color="#c026d3" hot={ov.mentors.pending > 0} go={() => { setMFilter("pending"); setTab("mentors"); }} />
              <Stat icon={FaUserCheck} label="Approved influencers" value={ov.mentors.approved} color="#6366f1" go={() => { setMFilter("approved"); setTab("mentors"); }} />
              <Stat icon={FaRupeeSign} label="Total collected" value={rs(ov.revenue.a)} color="#0f766e" />
              <Stat icon={FaWallet} label={`Your share (${100 - ov.share_pct}%)`} value={rs(ov.revenue.o)} color="#16a34a" />
              <Stat icon={FaWallet} label="Due to influencers" value={rs(ov.payout_due)} color="#e11d48" hot={ov.payout_due > 0} go={() => setTab("payouts")} />
            </div>

            <div className={`${CARD} p-5`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                  <FaBolt className="text-amber-500" /> Needs your attention
                </div>
                {ov.bookings.review > 5 ? (
                  <button className="text-xs font-bold text-violet-600" onClick={() => { setBFilter("review"); setTab("bookings"); }}>
                    See all {ov.bookings.review}
                  </button>
                ) : null}
              </div>
              {urgent.length ? (
                <div className="grid gap-2">
                  {urgent.map((b) => (
                    <button key={b.id} onClick={() => { setBFilter("review"); setTab("bookings"); }} className="flex items-center gap-3 rounded-xl p-3 bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100 text-left">
                      <Avatar name={b.full_name} size={38} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 dark:text-white truncate">{b.full_name}</div>
                        <div className="text-xs text-slate-500">
                          UTR {b.utr || "-"} · {ago(b.created_at)}
                        </div>
                      </div>
                      <div className="font-extrabold text-violet-700">{rs(b.amount)}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-slate-500 py-4 text-center">All clear. No payment is waiting for you.</div>
              )}
            </div>
          </div>
        ) : (
          <Empty loading={loading} text="No data" />
        )
      ) : null}

      {/* bookings */}
      {tab === "bookings" ? (
        <>
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            {BOOKING_FILTERS.map((f) => {
              const count = f.key && ov ? ov.bookings[f.key] : null;
              const on = bFilter === f.key;
              return (
                <button key={f.key || "all"} onClick={() => setBFilter(f.key)} className={`cs-btn inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-extrabold ${on ? "bg-slate-900 text-white" : "bg-white dark:bg-secondary-dark-bg text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700"}`}>
                  <f.icon /> {f.label}
                  {count !== null ? <span className={`px-1.5 rounded-full text-[10px] ${on ? "bg-white/25" : "bg-slate-100 dark:bg-slate-700"}`}>{count}</span> : null}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2 w-full md:w-auto">
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name, phone, UTR or #id" className={`${INPUT} px-3 py-2.5 flex-1 md:w-64`} />
              <button onClick={load} className={`${BTN} bg-violet-600`}>
                <FaSearch /> Search
              </button>
            </div>
          </div>
          <div className="grid gap-4">{bookings.length ? bookings.map((b) => <BookingCard key={b.id} b={b} ask={ask} call={call} reload={load} say={say} setShot={setShot} copy={copy} />) : <Empty loading={loading} text="No bookings in this list" />}</div>
        </>
      ) : null}

      {/* influencers */}
      {tab === "mentors" ? (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {MENTOR_FILTERS.map((f) => {
              const on = mFilter === f.key;
              return (
                <button key={f.key} onClick={() => setMFilter(f.key)} className={`cs-btn inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-extrabold ${on ? "bg-slate-900 text-white" : "bg-white dark:bg-secondary-dark-bg text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700"}`}>
                  {f.label}
                  <span className={`px-1.5 rounded-full text-[10px] ${on ? "bg-white/25" : "bg-slate-100 dark:bg-slate-700"}`}>{mCounts[f.key] || 0}</span>
                </button>
              );
            })}
          </div>
          <div className="grid gap-4">{mentors.length ? mentors.map((m) => <MentorCardAdmin key={m.id} m={m} ask={ask} call={call} reload={load} say={say} copy={copy} />) : <Empty loading={loading} text="Nobody in this list" />}</div>
        </>
      ) : null}

      {/* payouts */}
      {tab === "payouts" ? (
        <>
          <div className="rounded-2xl p-5 mb-4 text-white flex items-center justify-between cs-up" style={{ background: "linear-gradient(135deg,#e11d48,#f97316)" }}>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest opacity-80">Total due to influencers</div>
              <div className="text-xs opacity-90 mt-1">Pay each one, then tap "Mark paid" so the balance goes down.</div>
            </div>
            <div className="text-3xl font-extrabold">{rs(pay.total_pending)}</div>
          </div>
          <div className="grid gap-3">
            {pay.list.length ? (
              pay.list.map((p) => (
                <div key={p.mentor_id} className={`${CARD} cs-card cs-up p-4 md:p-5`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={p.name} />
                      <div>
                        <div className="font-extrabold text-slate-900 dark:text-white">{p.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                          UPI: {p.payout_upi}
                          <button onClick={() => copy(p.payout_upi)} className="text-slate-400 hover:text-violet-600">
                            <FaCopy size={11} />
                          </button>
                          · {p.completed} completed
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-5 text-center">
                      <div>
                        <div className="text-[11px] uppercase font-bold text-slate-400">Earned</div>
                        <div className="font-extrabold">{rs(p.earned)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase font-bold text-slate-400">Paid</div>
                        <div className="font-extrabold text-emerald-600">{rs(p.paid)}</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase font-bold text-slate-400">Due</div>
                        <div className="font-extrabold text-rose-600">{rs(p.pending)}</div>
                      </div>
                    </div>
                  </div>
                  {p.pending > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button className={`${BTN} bg-indigo-600`} onClick={() => payUpi(p)}>
                        <FaRupeeSign /> Pay via UPI app
                      </button>
                      <button className={`${BTN} bg-emerald-600`} onClick={() => ask({ title: `Record ${rs(p.pending)} as paid to ${p.name}?`, hint: "Write the payment UTR or a remark in the box (optional).", okLabel: "Mark paid", tone: "emerald", onOk: async (n) => { const d = await call("payout_mark", { mentor_id: p.mentor_id, amount: p.pending, note: n }); if (d.status) { say("success", "Payout recorded"); load(); } else say("error", d.error); } })}>
                        <FaCheck /> Mark paid
                      </button>
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <Empty loading={loading} text="No earnings yet. They appear when you mark a booking completed." />
            )}
          </div>
          {pay.history && pay.history.length ? (
            <div className={`${CARD} p-5 mt-4`}>
              <div className="font-extrabold mb-2 text-slate-900 dark:text-white">Recent payouts</div>
              {pay.history.map((h) => (
                <div key={h.id} className="flex justify-between text-sm py-2 border-t border-slate-100 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                  <span>
                    {h.full_name} {h.note ? <span className="text-slate-400">· {h.note}</span> : null}
                  </span>
                  <span className="font-bold">
                    {rs(h.amount)} <span className="text-slate-400 font-normal">· {String(h.created_at).slice(0, 10)}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {/* confirm dialog */}
      {dlg ? (
        <div className="fixed inset-0 z-[3000] bg-black/55 flex items-center justify-center p-4 cs-fade" onClick={() => setDlg(null)}>
          <div className={`${CARD} cs-zoom w-full max-w-md overflow-hidden`} onClick={(e) => e.stopPropagation()}>
            <div style={{ height: 5, background: tone[dlg.tone] || "#7c3aed" }} />
            <div className="p-5">
              <div className="font-extrabold text-lg text-slate-900 dark:text-white">{dlg.title}</div>
              {dlg.hint ? <div className="text-sm text-slate-500 mt-1">{dlg.hint}</div> : null}
              {dlg.quick ? (
                <div className="flex flex-wrap gap-2 mt-3">
                  {dlg.quick.map((x) => (
                    <button key={x} onClick={() => setNote(x)} className="px-3 py-1.5 rounded-full text-xs font-bold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-100 hover:bg-violet-100">
                      {x}
                    </button>
                  ))}
                </div>
              ) : null}
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={dlg.needNote ? "Reason (required, the user can see it)" : "Note (optional)"} className={`${INPUT} w-full p-3 mt-3`} />
              <div className="flex justify-end gap-2 mt-4">
                <button className={`${BTN} bg-slate-400`} onClick={() => setDlg(null)}>
                  Cancel
                </button>
                <button
                  className={BTN}
                  style={{ background: tone[dlg.tone] || "#7c3aed" }}
                  onClick={() => {
                    if (dlg.needNote && !note.trim()) return say("error", "Please write the reason");
                    const fn = dlg.onOk;
                    setDlg(null);
                    fn(note.trim());
                  }}>
                  {dlg.okLabel || "OK"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* screenshot viewer */}
      {shot ? (
        <div className="fixed inset-0 z-[3000] bg-black/85 flex flex-col items-center justify-center p-4 cs-fade" onClick={() => setShot("")}>
          <div className="flex gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
            <a href={shot} target="_blank" rel="noreferrer" className={`${BTN} bg-white/20`}>
              <FaExternalLinkAlt /> Open full size
            </a>
            <button className={`${BTN} bg-white/20`} onClick={() => setShot("")}>
              <FaTimes /> Close
            </button>
          </div>
          <img src={shot} alt="payment" onClick={(e) => e.stopPropagation()} className="cs-zoom max-h-[82vh] max-w-full rounded-2xl" />
        </div>
      ) : null}
    </div>
  );
};

export default CreatorSupportAdmin;
